import { z } from 'zod';

/**
 * Allowed page sizes — common web pattern. The "11" used by the original
 * mock-only page was arbitrary and has been removed.
 */
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

/**
 * Allowed sort keys exposed by the listing endpoint. `undefined` represents
 * the default state (no sort button selected) — the server falls back to
 * `createdAt DESC` in that case.
 */
export const SORT_KEYS = ['name', 'status', 'createdAt', 'updatedAt'] as const;
export type SortKey = (typeof SORT_KEYS)[number];

export const SORT_DIRECTIONS = ['asc', 'desc'] as const;
export type SortDirection = (typeof SORT_DIRECTIONS)[number];

export const VIEW_MODES = ['cards', 'table'] as const;
export type ViewMode = (typeof VIEW_MODES)[number];

/**
 * Zod schema for the `/companies` route's URL search params. Used as
 * TanStack Router's `validateSearch` so:
 *   - state is bookmarkable, share-linkable, reload-safe (FR-017, FR-018);
 *   - malformed URLs fall back to defaults (edge case in spec.md);
 *   - the search shape is the single source of truth for query construction.
 *
 * `.catch()` on every field means a malformed value (e.g. `?view=garbage`,
 * `?page=abc`) silently falls back to its default instead of throwing —
 * matches the convention from `apps/candidate/.../opportunities-search.schema.ts`.
 */
export const CompaniesSearchSchema = z
  .object({
    view: z.enum(VIEW_MODES).catch('cards').default('cards'),
    sort: z.enum(SORT_KEYS).optional().catch(undefined),
    dir: z.enum(SORT_DIRECTIONS).catch('asc').default('asc'),
    search: z.string().optional().catch(undefined),
    page: z.coerce.number().int().positive().catch(1).default(1),
    pageSize: z
      .union([z.literal(10), z.literal(25), z.literal(50), z.literal(100)])
      .catch(10)
      .default(10),
  })
  .strip();

export type CompaniesSearch = z.infer<typeof CompaniesSearchSchema>;

/** Default search params — equivalent to landing on `/companies` with no query string. */
export const DEFAULT_COMPANIES_SEARCH: CompaniesSearch = {
  view: 'cards',
  dir: 'asc',
  page: 1,
  pageSize: 10,
};
